import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { login } from '../src/actions/users';
import { EMAIL_REGEX } from '../src/constants';
import FuneriaIcon from '../src/icons/FuneriaIcon';
import { fetch } from '../src/utils/axios';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    minHeight: '100vh',
    background: theme.palette.common.white,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 320,
    padding: theme.spacing(4),
  },
}));

function Login() {
  const classes = useStyles();
  const router = useRouter();
  const [alertMessage, setAlertMessage] = useState(false);

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm();

  // Rufe /status auf, um zu prüfen, ob eine Authentifizierung bereits existiert
  // z.B. weil sie in einem anderen Tab stattgefunden hat.
  useQuery(['status'], {
    enabled: router.isReady, // wichtig, da ansonsten router.query leer ist
    refetchOnWindowFocus: true,
    queryFn: () => fetch('/status'),
    onSuccess: (data) => {
      if (data.authenticated) {
        Router.push(router.query.url ?? '/');
      }
    },
    // Überschreibe globalen Error-Handler, damit bei einer Antwort von 401
    // nicht wieder auf die Login-Seite umgeleitet wird, was dann zu einer Loop
    // führt.
    onError: () => {},
  });

  const onSubmit = async ({ email, password }) => {
    if (!EMAIL_REGEX.test(email)) {
      setError('email', { message: 'Die E-Mail ist ungültig' });
      return;
    }
    try {
      await login({ email, password });
      Router.push(router.query.url ?? '/');
    } catch (error) {
      if (error.status === 404) {
        setAlertMessage('E-Mail und Passwort stimmen nicht überein.');
      } else if (error.status === 401) {
        setAlertMessage('Nutzer ist deaktiviert');
      } else {
        setAlertMessage('Unbekannter Fehler');
      }
    }
  };

  return (
    <>
      <Head>
        <title>Anmelden</title>
      </Head>

      <form id="loginForm" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={classes.root}>
          <div className={classes.container}>
            <Box mb={4}>
              <FuneriaIcon width={128} height={128} />
            </Box>

            {alertMessage && (
              <Box mb={2}>
                <Alert severity="error">{alertMessage}</Alert>
              </Box>
            )}

            <Box width="100%" mb={2}>
              <Controller
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    type="email"
                    label="E-Mail"
                    fullWidth
                    helperText={errors?.email?.message}
                    error={Boolean(errors?.email)}
                    required
                  />
                )}
                control={control}
                rules={{
                  required: 'E-Mail ist erforderlich',
                }}
                name="email"
                defaultValue=""
              />
            </Box>

            <Box width="100%" mb={4}>
              <Controller
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    type="password"
                    label="Passwort"
                    fullWidth
                    helperText={errors?.password?.message}
                    error={Boolean(errors?.password)}
                    required
                  />
                )}
                control={control}
                rules={{
                  required: 'Passwort ist erforderlich',
                }}
                name="password"
                defaultValue=""
              />
            </Box>

            <Box mb={2}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                form="loginForm"
              >
                Anmelden
              </Button>
            </Box>

            <Box textAlign="center">
              <Link href="/passwort-vergessen">Passwort vergessen?</Link>
            </Box>
          </div>
        </div>
      </form>
    </>
  );
}

export default Login;
